import React, { useEffect, useMemo, useState } from "react";
import { Link, useSearchParams } from "react-router-dom";
import backIcon from "../assets/ecommerce/atras.png";
import cartIcon from "../assets/ecommerce/carrito.webp";
import { useCart } from "../context/CartContext";

const API_BASE_URL = (process.env.REACT_APP_API_BASE_URL || "http://localhost:89").replace(
  /\/$/,
  ""
);

const formatCurrency = new Intl.NumberFormat("es-MX", {
  style: "currency",
  currency: "MXN",
  minimumFractionDigits: 2,
});

const formatWholeNumber = new Intl.NumberFormat("es-MX", {
  maximumFractionDigits: 0,
});

const buildApiUrl = (path) => `${API_BASE_URL}${path}`;
const normalizeString = (value) => {
  if (value === null || value === undefined) return "";
  const normalized = String(value).trim();
  return normalized.length ? normalized : "";
};
const normalizeResinCode = (value) => normalizeString(value).toUpperCase();
const normalizeGrams = (value) => {
  const numeric = Number(value);
  return Number.isFinite(numeric) && numeric > 0 ? numeric : null;
};

const EcommerceResinaEpoxica = () => {
  const { addItem, openCart, shippingConfig } = useCart();
  const [searchParams] = useSearchParams();
  const [resins, setResins] = useState([]);
  const [selectedResin, setSelectedResin] = useState(null);
  const [selectedPresentationId, setSelectedPresentationId] = useState("");
  const [loadingResins, setLoadingResins] = useState(true);
  const [resinsError, setResinsError] = useState("");
  const [pricing, setPricing] = useState(null);
  const [pricingError, setPricingError] = useState("");
  const [pricingLoading, setPricingLoading] = useState(false);
  const [activeTab, setActiveTab] = useState("resin");
  const [resinImageSrc, setResinImageSrc] = useState(null);
  const requestedResinCode = useMemo(
    () => normalizeResinCode(searchParams.get("code")),
    [searchParams]
  );
  const requestedPresentationGrams = useMemo(
    () => normalizeGrams(searchParams.get("grams")),
    [searchParams]
  );

  useEffect(() => {
    let active = true;
    setLoadingResins(true);

    fetch(buildApiUrl("/api/ecommerce/resins/products"))
      .then(async (response) => {
        const contentType = response.headers.get("content-type") || "";
        const text = await response.text();
        if (!response.ok || !contentType.includes("application/json")) {
          throw new Error("No se pudieron cargar las resinas.");
        }
        try {
          return JSON.parse(text);
        } catch (error) {
          throw new Error("No se pudieron cargar las resinas.");
        }
      })
      .then((data) => {
        if (!active) return;
        setResins(data);
        const firstResin = data[0] || null;
        setSelectedResin(firstResin);
        if (firstResin?.presentations?.length) {
          setSelectedPresentationId(String(firstResin.presentations[0].id));
        } else {
          setSelectedPresentationId("");
        }
      })
      .catch((error) => {
        if (!active) return;
        setResinsError(error.message);
      })
      .finally(() => {
        if (!active) return;
        setLoadingResins(false);
      });

    return () => {
      active = false;
    };
  }, []);

  useEffect(() => {
    if (
      loadingResins ||
      !resins.length ||
      !requestedResinCode ||
      !requestedPresentationGrams
    ) {
      return;
    }

    const matchedResin = resins.find(
      (resin) => normalizeResinCode(resin.code) === requestedResinCode
    );

    if (!matchedResin) return;

    const matchedPresentation = (matchedResin.presentations || []).find(
      (presentation) => normalizeGrams(presentation.weight_grams) === requestedPresentationGrams
    );

    if (!matchedPresentation) return;

    setSelectedResin((current) => (current?.id === matchedResin.id ? current : matchedResin));
    setSelectedPresentationId(String(matchedPresentation.id));
    setActiveTab("summary");
  }, [loadingResins, requestedPresentationGrams, requestedResinCode, resins]);

  useEffect(() => {
    if (loadingResins || !selectedResin) {
      setResinImageSrc(null);
      return;
    }

    const src = selectedResin?.image_url;
    if (!src) {
      setResinImageSrc("/ecommerce/Resinas.png");
      return;
    }

    let active = true;
    setResinImageSrc(null);

    const img = new Image();
    img.onload = () => {
      if (!active) return;
      setResinImageSrc(src);
    };
    img.onerror = () => {
      if (!active) return;
      setResinImageSrc("/ecommerce/Resinas.png");
    };
    img.src = src;

    return () => {
      active = false;
    };
  }, [loadingResins, selectedResin]);

  const selectedPresentation = useMemo(() => {
    if (!selectedResin) return null;
    return (
      selectedResin.presentations?.find(
        (presentation) => String(presentation.id) === String(selectedPresentationId)
      ) || null
    );
  }, [selectedResin, selectedPresentationId]);

  const safeGrams = selectedPresentation ? Number(selectedPresentation.weight_grams) : 0;
  const hasGrams = safeGrams > 0;
  const fallbackCatalystRatio = useMemo(() => {
    const resinCode = String(selectedResin?.code || "").toUpperCase();
    return resinCode === "RN1" ? 0.15 : 0.5;
  }, [selectedResin]);
  const fallbackCatalystGrams = safeGrams * fallbackCatalystRatio;
  const catalystGrams = useMemo(() => {
    const backendCatalyst = Number(pricing?.catalyst_grams);
    if (Number.isFinite(backendCatalyst)) {
      return backendCatalyst;
    }
    return fallbackCatalystGrams;
  }, [pricing, fallbackCatalystGrams]);
  const formattedSafeGrams = useMemo(
    () => formatWholeNumber.format(safeGrams),
    [safeGrams]
  );
  const formattedCatalystGrams = useMemo(
    () => formatWholeNumber.format(catalystGrams),
    [catalystGrams]
  );
  const summaryItems = useMemo(() => {
    if (!selectedResin || !hasGrams) return [];
    return [
      `RESINA ${selectedResin.type} (${formattedSafeGrams} G).`,
      `CATALIZADOR (${formattedCatalystGrams} G).`,
    ];
  }, [selectedResin, hasGrams, formattedSafeGrams, formattedCatalystGrams]);

  useEffect(() => {
    setPricing(null);
    setPricingError("");

    if (!selectedResin || !selectedPresentation || !hasGrams) {
      return;
    }

    const controller = new AbortController();
    const timeoutId = setTimeout(() => {
      setPricingLoading(true);

      fetch(buildApiUrl("/api/ecommerce/resins/pricing"), {
        method: "POST",
        headers: {
          "Content-Type": "application/json",
        },
        body: JSON.stringify({
          resin_id: selectedResin.id,
          presentation_id: selectedPresentation.id,
        }),
        signal: controller.signal,
      })
        .then(async (response) => {
          const contentType = response.headers.get("content-type") || "";
          const text = await response.text();
          if (!response.ok || !contentType.includes("application/json")) {
            throw new Error("No se pudo calcular el precio.");
          }
          try {
            return JSON.parse(text);
          } catch (error) {
            throw new Error("No se pudo calcular el precio.");
          }
        })
        .then((data) => {
          setPricing(data);
        })
        .catch((error) => {
          if (error.name === "AbortError") return;
          setPricingError(error.message);
        })
        .finally(() => {
          setPricingLoading(false);
        });
    }, 350);

    return () => {
      controller.abort();
      clearTimeout(timeoutId);
    };
  }, [selectedResin, selectedPresentation, safeGrams, hasGrams]);

  const summary = useMemo(() => {
    if (!pricing) return "--";
    return formatCurrency.format(pricing.final_cost || 0);
  }, [pricing]);

  const shippingLabel = useMemo(() => {
    if (!pricing) return "--";
    const finalCost = Number(pricing.final_cost);
    if (!Number.isFinite(finalCost)) return "--";
    const threshold = shippingConfig?.threshold ?? 499;
    const cost = shippingConfig?.cost ?? 199;
    return finalCost >= threshold ? "Gratis" : formatCurrency.format(cost);
  }, [pricing, shippingConfig]);
  const technicalInformation = normalizeString(selectedResin?.technical_information);
  const presentationsText = normalizeString(selectedResin?.presentations_text);
  const mix = normalizeString(selectedResin?.mix);
  const processCompatibility = normalizeString(selectedResin?.compatibility);
  const hasCommercialDetails =
    technicalInformation || presentationsText || mix || processCompatibility;

  const handleAddToCart = () => {
    if (!selectedResin || !selectedPresentation || !hasGrams || !pricing) return;
    addItem({
      type: "resina-epoxica",
      name: `${selectedResin.type} (${formattedSafeGrams} G) + CATALIZADOR (${formattedCatalystGrams} G)`,
      unitPrice: pricing.final_cost || 0,
      payload: {
        resin_id: selectedResin.id,
        presentation_id: selectedPresentation.id,
        resin_grams: safeGrams,
        catalyst_grams: catalystGrams,
      },
    });
    openCart();
  };

  const goPrevTab = () => {
    if (activeTab === "summary") {
      setActiveTab("resin");
    }
  };

  const goNextTab = () => {
    if (activeTab === "resin") {
      setActiveTab("summary");
    }
  };

  return (
    <div className="relative px-8 md:px-14 py-6 md:py-8 overflow-hidden h-auto md:h-full flex flex-col bg-white">
      <div className="w-full max-w-[1400px] mx-auto flex flex-col flex-1">
        <div className="grid grid-cols-2 grid-rows-[auto,auto] items-center gap-3 md:gap-4 mb-5 md:mb-6 md:grid-cols-[160px,1fr,160px] md:grid-rows-1">
          <Link
            to="/ecommerce"
            className="inline-flex items-center gap-2 text-gray-500 hover:text-gray-800 justify-self-start"
          >
            <img src={backIcon} alt="Atrás" className="w-8 h-8 md:w-10 md:h-10" />
            <span className="text-sm md:text-base font-semibold">Atrás</span>
          </Link>
          <button
            type="button"
            onClick={openCart}
            className="justify-self-end cursor-pointer md:col-start-3"
          >
            <img src={cartIcon} alt="Carrito" className="w-14 h-14 md:w-16 md:h-16" />
          </button>
          <h1 className="col-span-2 row-start-2 text-xl md:text-3xl lg:text-4xl font-extrabold uppercase tracking-tight text-gray-900 text-center max-w-[420px] md:max-w-[1000px] w-full justify-self-center leading-tight md:col-span-1 md:col-start-2 md:row-start-1">
            Compra resina con la cantidad que necesitas
          </h1>
        </div>

        <div className="flex flex-col md:flex-row items-start gap-2 md:gap-3 flex-1 min-h-0">
          <div className="md:w-[50%] w-full h-[38vh] md:h-full shrink-0">
            <div className="relative h-full flex items-start justify-end md:pt-2">
              <div className="relative inline-flex items-start justify-start w-[520px] h-[320px] md:w-[620px] md:h-[500px] overflow-visible">
                {resinImageSrc && (
                  <img
                    src={resinImageSrc}
                    alt={selectedResin?.type || "Resina"}
                    className="block w-full h-full object-contain"
                  />
                )}
              </div>
            </div>
          </div>

          <div className="md:w-[50%] w-full md:flex-1 min-h-0 md:h-full">
            <div className="h-full min-h-0 flex flex-col">
              <div className="flex items-center justify-between bg-black text-white px-4 py-2 rounded-lg">
                <button
                  type="button"
                  className="text-white/70"
                  aria-label="Paso anterior"
                  onClick={goPrevTab}
                >
                  <img src={backIcon} alt="Atrás" className="w-5 h-5 md:w-6 md:h-6" />
                </button>
                <div className="flex items-center justify-evenly flex-1 mx-4">
                  <button
                    type="button"
                    onClick={() => setActiveTab("resin")}
                    className={`px-4 py-1 text-xs font-bold rounded-sm shadow-sm ${
                      activeTab === "resin" ? "bg-white text-black" : "text-white/50"
                    }`}
                  >
                    RESINA
                  </button>
                  <button
                    type="button"
                    onClick={() => setActiveTab("summary")}
                    className={`px-4 py-1 text-xs font-bold rounded-sm shadow-sm ${
                      activeTab === "summary" ? "bg-white text-black" : "text-white/50"
                    }`}
                  >
                    RESUMEN
                  </button>
                </div>
                <button
                  type="button"
                  className="text-white/70"
                  aria-label="Paso siguiente"
                  onClick={goNextTab}
                >
                  <img
                    src={backIcon}
                    alt="Siguiente"
                    className="w-5 h-5 md:w-6 md:h-6 rotate-180"
                  />
                </button>
              </div>

              <div className="flex-1 min-h-0 overflow-y-auto px-4 md:px-5 py-4 pb-10 md:pb-12 space-y-4">
                {activeTab === "resin" ? (
                  <>
                    <div className="rounded-xl border border-black/30 bg-white/80 px-3 py-3 space-y-3">
                      <h2 className="text-sm md:text-base font-semibold text-gray-900 text-left">
                        Selecciona el tipo de Resina Omova:
                      </h2>
                      {loadingResins ? (
                        <div className="text-sm text-gray-500">Cargando resinas...</div>
                      ) : resinsError ? (
                        <div className="text-xs text-red-600 font-semibold">{resinsError}</div>
                      ) : (
                        <div className="grid grid-cols-2 gap-2">
                          {resins.map((resin) => {
                            const isSelected = selectedResin?.id === resin.id;
                            return (
                              <button
                                key={resin.id}
                                type="button"
                                onClick={() => {
                                  setSelectedResin(resin);
                                  if (resin.presentations?.length) {
                                    setSelectedPresentationId(String(resin.presentations[0].id));
                                  } else {
                                    setSelectedPresentationId("");
                                  }
                                }}
                                className={`px-3 py-1.5 text-[11px] md:text-xs font-bold uppercase tracking-wide rounded-full border transition ${
                                  isSelected
                                    ? "bg-white border-black text-primary-green shadow"
                                    : "bg-black border-black text-white hover:bg-gray-900"
                                }`}
                              >
                                {resin.type}
                              </button>
                            );
                          })}
                        </div>
                      )}
                    </div>

                    <div className="rounded-xl border border-black/30 bg-white/80 px-3 py-3 space-y-3">
                      <h3 className="text-sm md:text-base font-semibold text-gray-900 text-left">
                        Cantidad requerida:
                      </h3>
                      <div className="flex flex-wrap items-center gap-3 text-sm md:text-base">
                        <div className="flex items-center gap-2 bg-black text-white px-3 py-1.5 rounded-lg">
                          <select
                            value={selectedPresentationId}
                            onChange={(event) => setSelectedPresentationId(event.target.value)}
                            className="w-24 bg-transparent text-white font-bold text-center outline-none tabular-nums"
                            disabled={!selectedResin?.presentations?.length}
                          >
                            {(selectedResin?.presentations || []).map((presentation) => (
                              <option
                                key={presentation.id}
                                value={presentation.id}
                                className="text-black"
                              >
                                {formatWholeNumber.format(presentation.weight_grams || 0)}
                              </option>
                            ))}
                          </select>
                          <span className="font-semibold">g</span>
                          <span className="font-semibold">resina</span>
                        </div>
                        <span className="text-lg font-bold text-gray-700">=</span>
                        <div className="flex items-center gap-2 bg-gray-200 text-gray-700 px-3 py-1.5 rounded-lg">
                          <span className="w-20 text-center font-bold tabular-nums">
                            {Number.isFinite(catalystGrams) ? formattedCatalystGrams : "0"}
                          </span>
                          <span className="font-semibold">g</span>
                          <span className="font-semibold">catalizador</span>
                        </div>
                      </div>
                      {pricingError && (
                        <p className="text-xs text-red-600 font-semibold">{pricingError}</p>
                      )}
                    </div>

                    <div className="rounded-xl bg-dark-gray text-white px-4 py-3 space-y-1">
                      <div className="flex items-center justify-between text-sm">
                        <span>Subtotal:</span>
                        <span className="font-bold">
                          {pricingLoading ? "Calculando..." : summary}
                        </span>
                      </div>
                      <div className="flex items-center justify-between text-sm text-white/70">
                        <span>Envío:</span>
                        <span>{pricingLoading ? "Calculando..." : shippingLabel}</span>
                      </div>
                    </div>

                    <div className="flex justify-end pb-2">
                      <button
                        type="button"
                        disabled={!hasGrams || !selectedResin || !selectedPresentation}
                        onClick={() => setActiveTab("summary")}
                        className="px-6 py-2 rounded-full bg-primary-green text-white font-bold shadow-md disabled:opacity-50 disabled:cursor-not-allowed"
                      >
                        Siguiente
                      </button>
                    </div>
                  </>
                ) : (
                  <div className="space-y-6 text-left">
                    <div className="space-y-2 w-full">
                      <div className="text-2xl md:text-3xl font-extrabold uppercase text-gray-900">
                        {selectedResin?.type || "RESINA"}
                      </div>
                      <div className="text-base md:text-lg font-semibold text-gray-600 uppercase">
                        ({formattedSafeGrams} G)
                      </div>
                    </div>

                    <div className="flex flex-col md:flex-row md:items-start md:justify-between gap-6">
                      <div className="space-y-2">
                        <div className="text-base md:text-lg font-semibold text-gray-700">
                          Envío:
                        </div>
                        <div
                          className={`text-2xl md:text-3xl font-extrabold ${
                            shippingLabel === "Gratis" ? "text-primary-green" : "text-gray-900"
                          }`}
                        >
                          {pricingLoading ? "Calculando..." : shippingLabel}
                        </div>

                        <div className="pt-3 space-y-1">
                          <div className="text-base md:text-lg font-semibold text-gray-700">
                            Subtotal:
                          </div>
                          <div className="text-3xl md:text-4xl font-extrabold text-gray-900">
                            {pricingLoading ? "Calculando..." : summary}
                          </div>
                        </div>
                      </div>

                    <div className="flex flex-col gap-3 md:items-end">
                      <button
                        type="button"
                        onClick={handleAddToCart}
                        className="px-8 py-2.5 rounded-full bg-pink-500 text-white font-bold shadow-md w-fit text-base md:text-lg"
                      >
                        Añadir al carrito
                      </button>
                    </div>
                    </div>

                    <div className="text-xl md:text-2xl font-extrabold text-gray-900">RESUMEN</div>
                    <div className="rounded-xl bg-dark-gray text-white px-5 py-4 text-base space-y-2">
                      {summaryItems.length ? (
                        summaryItems.map((item, index) => (
                          <div key={`${item}-${index}`} className="flex items-start gap-2">
                            <span>•</span>
                            <span>{item}</span>
                          </div>
                        ))
                      ) : (
                        <div>• Completa tus selecciones para ver el resumen.</div>
                      )}
                    </div>
                    {hasCommercialDetails && (
                      <div className="rounded-xl border border-black/20 bg-white px-4 py-4 text-sm md:text-base text-gray-800 space-y-2">
                        {technicalInformation && (
                          <p>
                            <span className="font-bold">Información técnica:</span>{" "}
                            {technicalInformation}
                          </p>
                        )}
                        {presentationsText && (
                          <p>
                            <span className="font-bold">Presentaciones:</span>{" "}
                            {presentationsText}
                          </p>
                        )}
                        {mix && (
                          <p>
                            <span className="font-bold">Mezcla:</span> {mix}
                          </p>
                        )}
                        {processCompatibility && (
                          <p>
                            <span className="font-bold">Compatibilidad de procesos:</span>{" "}
                            {processCompatibility}
                          </p>
                        )}
                      </div>
                    )}
                  </div>
                )}
              </div>
            </div>
          </div>
        </div>
      </div>
    </div>
  );
};

export default EcommerceResinaEpoxica;
